/**
 * Telefonos en formato E.164 (ADR-015).
 *
 * Se guarda siempre la forma internacional (`+598...`), aunque en el mostrador se
 * escriba local (`099 000 000`). El pais por defecto es Uruguay, y se acepta cualquier
 * numero internacional escrito con `+`.
 *
 * **El telefono no identifica al cliente**: familias, parejas y equipos comparten
 * numero. Buscar por telefono puede devolver varios clientes y la interfaz debe
 * resolver la ambiguedad, no quedarse con el primero (ADR-015). Quien identifica es la
 * cedula (ADR-014).
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { errorDominio } from '../tipos/errores.js';
import { exito, fallo } from '../tipos/resultado.js';
/** Pais por defecto cuando el numero se escribe en forma local. */
export const PAIS_POR_DEFECTO = 'UY';
/** Prefijo telefonico de Uruguay. */
export const PREFIJO_URUGUAY = '+598';
/**
 * Normaliza un telefono a E.164.
 *
 * Acepta la forma local uruguaya (`099000000`, `099 000 000`, `(099) 000-000`) y
 * cualquier numero internacional que empiece con `+`. Rechaza lo que no sea un numero
 * marcable de verdad.
 */
export function normalizarTelefono(entrada, campo = 'telefono', paisPorDefecto = PAIS_POR_DEFECTO) {
    const recortada = entrada.trim();
    if (recortada === '') {
        return fallo(errorDominio('TELEFONO_INVALIDO', campo));
    }
    const analizado = parsePhoneNumberFromString(recortada, paisPorDefecto);
    if (analizado?.isValid() !== true) {
        return fallo(errorDominio('TELEFONO_INVALIDO', campo));
    }
    return exito(analizado.number);
}
/** Texto E.164 del telefono. Para persistir. */
export function valorTelefono(telefono) {
    return telefono;
}
/** El telefono pertenece a Uruguay. */
export function esUruguayo(telefono) {
    return telefono.startsWith(PREFIJO_URUGUAY);
}
/**
 * Enmascara un telefono, dejando visibles el prefijo de pais y los tres ultimos digitos.
 *
 * `+59899000000` se convierte en `+598*****000`. Igual que con la cedula, los logs no
 * imprimen telefonos completos (docs/SEGURIDAD.md §6).
 */
export function enmascararTelefono(telefono) {
    const visiblesFinales = 3;
    // Prefijo de pais: entre 1 y 3 digitos despues del '+'.
    const prefijo = `+${parsePhoneNumberFromString(telefono)?.countryCallingCode ?? ''}`;
    const resto = telefono.slice(prefijo.length);
    if (resto.length <= visiblesFinales) {
        return `${prefijo}${'*'.repeat(resto.length)}`;
    }
    return `${prefijo}${'*'.repeat(resto.length - visiblesFinales)}${resto.slice(-visiblesFinales)}`;
}
/**
 * Formatea un telefono para mostrarlo: `+598 99 000 000`.
 *
 * Solo para pantallas. No usar en logs ni exportaciones.
 */
export function formatearTelefono(telefono) {
    const analizado = parsePhoneNumberFromString(telefono);
    return analizado === undefined ? telefono : analizado.formatInternational();
}
//# sourceMappingURL=telefono.js.map